import { cn } from '@/utils/cn';

interface Phase {
  phase_id?: string;
  label: string;
  phase_type?: string;
  start_frame: number;
  end_frame: number;
  importance?: string;
  description?: string;
}

interface ClickablePhaseTimelineProps {
  phases: Phase[];
  totalFrames: number;
  currentFrame?: number;
  selectedPhaseId?: string | null;
  onPhaseClick?: (phase: Phase) => void;
  className?: string;
  fps?: number;
}

// Phase colors based on index
const PHASE_COLORS = [
  { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', legend: 'bg-blue-500' },
  { bg: 'bg-green-500', hover: 'hover:bg-green-600', legend: 'bg-green-500' },
  { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600', legend: 'bg-yellow-500' },
  { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', legend: 'bg-purple-500' },
  { bg: 'bg-pink-500', hover: 'hover:bg-pink-600', legend: 'bg-pink-500' },
  { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600', legend: 'bg-indigo-500' },
  { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', legend: 'bg-orange-500' },
  { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', legend: 'bg-teal-500' },
];

// Importance badge colors
const IMPORTANCE_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-dark-elevated-1 text-text-secondary border-border-subtle',
};

function ClickablePhaseTimeline({
  phases,
  totalFrames,
  currentFrame = 0,
  selectedPhaseId,
  onPhaseClick,
  className,
  fps = 30,
}: ClickablePhaseTimelineProps) {
  if (phases.length === 0 || totalFrames === 0) {
    return (
      <div className={cn('text-center py-4 text-text-muted', className)}>
        No phases defined
      </div>
    );
  }

  // Calculate current frame position as percentage
  const currentPosition = (currentFrame / totalFrames) * 100;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Timeline bar with clickable phases */}
      <div className="relative">
        <div className="h-14 bg-dark-elevated-2 rounded-lg overflow-hidden relative">
          {phases.map((phase, index) => {
            const phaseWidth = ((phase.end_frame - phase.start_frame) / totalFrames) * 100;
            const phaseStart = (phase.start_frame / totalFrames) * 100;
            const colors = PHASE_COLORS[index % PHASE_COLORS.length];
            const isSelected = selectedPhaseId === (phase.phase_id || `phase-${index}`);

            return (
              <button
                key={phase.phase_id || `phase-${index}`}
                className={cn(
                  'absolute h-full transition-all cursor-pointer',
                  colors.bg,
                  colors.hover,
                  isSelected ? 'ring-2 ring-offset-1 ring-blue-600 opacity-100' : 'opacity-75 hover:opacity-100'
                )}
                style={{
                  left: `${phaseStart}%`,
                  width: `${phaseWidth}%`,
                }}
                onClick={() => onPhaseClick?.(phase)}
                title={`${phase.label}: Frames ${phase.start_frame}-${phase.end_frame}`}
              >
                {/* Phase label inside the bar (if wide enough) */}
                {phaseWidth > 15 && (
                  <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium truncate px-1">
                    {phase.label}
                  </span>
                )}
              </button>
            );
          })}

          {/* Current frame indicator */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
            style={{ left: `${currentPosition}%` }}
          >
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-500" />
          </div>
        </div>

        {/* Frame markers */}
        <div className="flex justify-between mt-1 text-xs text-text-muted">
          <span>0</span>
          <span>Frame {currentFrame}</span>
          <span>{totalFrames}</span>
        </div>
      </div>

      {/* Phase legend with importance badges */}
      <div className="flex flex-wrap gap-3">
        {phases.map((phase, index) => {
          const colors = PHASE_COLORS[index % PHASE_COLORS.length];
          const isSelected = selectedPhaseId === (phase.phase_id || `phase-${index}`);
          const durationSec = ((phase.end_frame - phase.start_frame) / fps).toFixed(1);

          return (
            <button
              key={phase.phase_id || `phase-${index}`}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all',
                isSelected
                  ? 'bg-dark-elevated-1 border-accent ring-1 ring-accent'
                  : 'bg-dark-card border-border-subtle hover:bg-dark-elevated-2 hover:border-border-strong'
              )}
              onClick={() => onPhaseClick?.(phase)}
            >
              <div className={cn('w-3 h-3 rounded', colors.legend)} />
              <span className="text-sm font-medium">{phase.label}</span>
              <span className="text-xs text-text-muted">({durationSec}s)</span>
              {phase.importance && (
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded border',
                    IMPORTANCE_COLORS[phase.importance] || IMPORTANCE_COLORS.medium
                  )}
                >
                  {phase.importance}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ClickablePhaseTimeline;
